import React from "react";
import { useTranslation } from "react-i18next";
import { SketchPicker } from "react-color";
import {
    Card,
    SectionTitle,
    LanguageTabs,
    LanguageTab,
    InputRow,
    InputLabel,
    Input,
    InputWrapper,
    VisibilityButton,
    ColorPickerButton,
    ColorPickerPopover,
    ColorPickerCover,
    ResetButton,
    ResetAllButton,
} from "./styled";

type TitleDescriptionProps = {
    mainColor: string;
    selectedLanguage: string;
    onLanguageChange: (lang: string) => void;
    languages: Array<{ code: string; label: string }>;
    defaultTranslations: Record<string, { title: string; description: string }>;
    customTexts: Record<string, { title: string; description: string }>;
    onCustomTitleChange: (value: string) => void;
    onCustomDescriptionChange: (value: string) => void;
    titleVisible: boolean;
    onTitleVisibleChange: () => void;
    descriptionVisible: boolean;
    onDescriptionVisibleChange: () => void;
    titleColor: string;
    onTitleColorChange: (color: string) => void;
    descriptionColor: string;
    onDescriptionColorChange: (color: string) => void;
    onResetTitle: () => void;
    onResetDescription: () => void;
    onResetAll: () => void;
};

export function TitleDescription({
    mainColor,
    selectedLanguage,
    onLanguageChange,
    languages,
    defaultTranslations,
    customTexts,
    onCustomTitleChange,
    onCustomDescriptionChange,
    titleVisible,
    onTitleVisibleChange,
    descriptionVisible,
    onDescriptionVisibleChange,
    titleColor,
    onTitleColorChange,
    descriptionColor,
    onDescriptionColorChange,
    onResetTitle,
    onResetDescription,
    onResetAll,
}: TitleDescriptionProps): JSX.Element {
    const { t } = useTranslation();

    // Color picker UI state (local to this component)
    const [showTitleColorPicker, setShowTitleColorPicker] = React.useState(false);
    const [showDescriptionColorPicker, setShowDescriptionColorPicker] = React.useState(false);
    const [titlePickerPosition, setTitlePickerPosition] = React.useState({ top: 0, left: 0 });
    const [descPickerPosition, setDescPickerPosition] = React.useState({ top: 0, left: 0 });
    const titleColorButtonRef = React.useRef<HTMLButtonElement>(null);
    const descColorButtonRef = React.useRef<HTMLButtonElement>(null);

    return (
        <Card data-testid="scan-to-banner-title-description-section">
            <SectionTitle style={{ marginBottom: "15px" }}>{t("Title and Description")}</SectionTitle>

            <LanguageTabs data-testid="scan-to-banner-language-tabs">
                {languages.map((lang) => (
                    <LanguageTab
                        data-testid={`scan-to-banner-language-tab-${lang.code}`}
                        key={lang.code}
                        isActive={selectedLanguage === lang.code}
                        mainColor={mainColor}
                        onClick={() => onLanguageChange(lang.code)}
                    >
                        {lang.label}
                    </LanguageTab>
                ))}
            </LanguageTabs>

            <div style={{ borderTop: "1px solid #e0e0e0", paddingTop: "10px" }}>
                <InputRow style={{ borderBottom: "1px solid #e0e0e0", paddingBottom: "10px", marginBottom: "10px" }}>
                    <InputLabel></InputLabel>
                    <InputLabel style={{ fontWeight: 500 }}>{t("Default text")}</InputLabel>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
                        <InputLabel style={{ fontWeight: 500 }}>{t("Customized")}</InputLabel>
                        <ResetAllButton data-testid="scan-to-banner-reset-all" mainColor={mainColor} onClick={onResetAll}>
                            <svg viewBox="0 0 24 24" fill="currentColor">
                                <path d="M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z"/>
                            </svg>
                            {t("Reset")}
                        </ResetAllButton>
                    </div>
                </InputRow>
                <InputRow data-testid="scan-to-banner-title-row">
                    <InputLabel>{t("Title")}</InputLabel>
                    <Input
                        data-testid="scan-to-banner-default-title-input"
                        value={defaultTranslations[selectedLanguage].title}
                        readOnly
                        style={{ backgroundColor: "#f8f9fa", cursor: "not-allowed" }}
                    />
                    <InputWrapper>
                        <Input
                            data-testid="scan-to-banner-custom-title-input"
                            value={customTexts[selectedLanguage].title}
                            onChange={(e) => onCustomTitleChange(e.target.value)}
                            placeholder={defaultTranslations[selectedLanguage].title}
                        />
                        <VisibilityButton
                            data-testid="scan-to-banner-title-visibility-toggle"
                            isVisible={titleVisible}
                            onClick={onTitleVisibleChange}
                            title={titleVisible ? "Masquer le titre" : "Afficher le titre"}
                        >
                            {titleVisible ? (
                                <svg viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/>
                                </svg>
                            ) : (
                                <svg viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M12 7c2.76 0 5 2.24 5 5 0 .65-.13 1.26-.36 1.83l2.92 2.92c1.51-1.26 2.7-2.89 3.43-4.75-1.73-4.39-6-7.5-11-7.5-1.4 0-2.74.25-3.98.7l2.16 2.16C10.74 7.13 11.35 7 12 7zM2 4.27l2.28 2.28.46.46C3.08 8.3 1.78 10.02 1 12c1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.38-.84l.42.42L19.73 22 21 20.73 3.27 3 2 4.27zM7.53 9.8l1.55 1.55c-.05.21-.08.43-.08.65 0 1.66 1.34 3 3 3 .22 0 .44-.03.65-.08l1.55 1.55c-.67.33-1.41.53-2.2.53-2.76 0-5-2.24-5-5 0-.79.2-1.53.53-2.2zm4.31-.78l3.15 3.15.02-.16c0-1.66-1.34-3-3-3l-.17.01z"/>
                                </svg>
                            )}
                        </VisibilityButton>
                        <ColorPickerButton
                            data-testid="scan-to-banner-title-color-picker"
                            ref={titleColorButtonRef}
                            color={titleColor}
                            onClick={(e) => {
                                const rect = (e.target as HTMLElement).getBoundingClientRect();
                                const pickerHeight = 260; // SketchPicker height + padding
                                const spaceBelow = window.innerHeight - rect.bottom;
                                const shouldPositionAbove = spaceBelow < pickerHeight;

                                setTitlePickerPosition({
                                    top: shouldPositionAbove
                                        ? rect.top + window.scrollY - pickerHeight
                                        : rect.bottom + window.scrollY,
                                    left: rect.left + window.scrollX
                                });
                                setShowTitleColorPicker(!showTitleColorPicker);
                            }}
                            title={t("Change title color")}
                        />
                        {showTitleColorPicker && (
                            <>
                                <ColorPickerCover data-testid="scan-to-banner-title-color-picker-cover" onClick={() => setShowTitleColorPicker(false)} />
                                <ColorPickerPopover data-testid="scan-to-banner-title-color-picker-popover" style={{ top: titlePickerPosition.top, left: titlePickerPosition.left }}>
                                    <SketchPicker
                                        color={titleColor}
                                        onChange={(color) => onTitleColorChange(color.hex)}
                                    />
                                </ColorPickerPopover>
                            </>
                        )}
                        <ResetButton data-testid="scan-to-banner-reset-title" mainColor={mainColor} onClick={onResetTitle} title={t("Reset")}>
                            <svg viewBox="0 0 24 24" fill="currentColor">
                                <path d="M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z"/>
                            </svg>
                        </ResetButton>
                    </InputWrapper>
                </InputRow>
                <InputRow data-testid="scan-to-banner-description-row">
                    <InputLabel>{t("Description")}</InputLabel>
                    <Input
                        data-testid="scan-to-banner-default-description-input"
                        value={defaultTranslations[selectedLanguage].description}
                        readOnly
                        style={{ backgroundColor: "#f8f9fa", cursor: "not-allowed" }}
                    />
                    <InputWrapper>
                        <Input
                            data-testid="scan-to-banner-custom-description-input"
                            value={customTexts[selectedLanguage].description}
                            onChange={(e) => onCustomDescriptionChange(e.target.value)}
                            placeholder={defaultTranslations[selectedLanguage].description}
                        />
                        <VisibilityButton
                            data-testid="scan-to-banner-description-visibility-toggle"
                            isVisible={descriptionVisible}
                            onClick={onDescriptionVisibleChange}
                            title={descriptionVisible ? "Masquer la description" : "Afficher la description"}
                        >
                            {descriptionVisible ? (
                                <svg viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/>
                                </svg>
                            ) : (
                                <svg viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M12 7c2.76 0 5 2.24 5 5 0 .65-.13 1.26-.36 1.83l2.92 2.92c1.51-1.26 2.7-2.89 3.43-4.75-1.73-4.39-6-7.5-11-7.5-1.4 0-2.74.25-3.98.7l2.16 2.16C10.74 7.13 11.35 7 12 7zM2 4.27l2.28 2.28.46.46C3.08 8.3 1.78 10.02 1 12c1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.38-.84l.42.42L19.73 22 21 20.73 3.27 3 2 4.27zM7.53 9.8l1.55 1.55c-.05.21-.08.43-.08.65 0 1.66 1.34 3 3 3 .22 0 .44-.03.65-.08l1.55 1.55c-.67.33-1.41.53-2.2.53-2.76 0-5-2.24-5-5 0-.79.2-1.53.53-2.2zm4.31-.78l3.15 3.15.02-.16c0-1.66-1.34-3-3-3l-.17.01z"/>
                                </svg>
                            )}
                        </VisibilityButton>
                        <ColorPickerButton
                            data-testid="scan-to-banner-description-color-picker"
                            ref={descColorButtonRef}
                            color={descriptionColor}
                            onClick={(e) => {
                                const rect = (e.target as HTMLElement).getBoundingClientRect();
                                const pickerHeight = 260; // SketchPicker height + padding
                                const spaceBelow = window.innerHeight - rect.bottom;
                                const shouldPositionAbove = spaceBelow < pickerHeight;

                                setDescPickerPosition({
                                    top: shouldPositionAbove
                                        ? rect.top + window.scrollY - pickerHeight
                                        : rect.bottom + window.scrollY,
                                    left: rect.left + window.scrollX
                                });
                                setShowDescriptionColorPicker(!showDescriptionColorPicker);
                            }}
                            title={t("Change description color")}
                        />
                        {showDescriptionColorPicker && (
                            <>
                                <ColorPickerCover data-testid="scan-to-banner-description-color-picker-cover" onClick={() => setShowDescriptionColorPicker(false)} />
                                <ColorPickerPopover data-testid="scan-to-banner-description-color-picker-popover" style={{ top: descPickerPosition.top, left: descPickerPosition.left }}>
                                    <SketchPicker
                                        color={descriptionColor}
                                        onChange={(color) => onDescriptionColorChange(color.hex)}
                                    />
                                </ColorPickerPopover>
                            </>
                        )}
                        <ResetButton data-testid="scan-to-banner-reset-description" mainColor={mainColor} onClick={onResetDescription} title={t("Reset")}>
                            <svg viewBox="0 0 24 24" fill="currentColor">
                                <path d="M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z"/>
                            </svg>
                        </ResetButton>
                    </InputWrapper>
                </InputRow>
            </div>
        </Card>
    );
}
